// frontend/src/tabs/TabLink.jsx
//
// Drop-in replacement for react-router's <Link> that routes clicks
// through useTabs() instead of directly into the router. Encapsulates:
//
//   - Plain left-click → open or focus (single-instance navigation)
//   - Ctrl/Cmd/Shift-click → forceNew (duplicate even if path is open)
//   - Middle-click (auxClick button 1) → forceNew
//   - Browser context-menu / right-click → falls through to default (so
//     "open in new browser window" still works for accessibility)
//   - Cap reached → emits the cap-toast event; the URL stays put
//
// Keeps the visual contract of <Link>: renders an <a href=...> so screen
// readers, hover-URL preview, and middle-click semantics all behave as
// users expect for a navigation control.

import { forwardRef, useCallback } from 'react';

import { useTabs } from './useTabs';

const TabLink = forwardRef(function TabLink(
  { to, className, children, onClick: onClickProp, ...rest },
  ref,
) {
  const { openTab } = useTabs();

  // URL ↔ activeId sync is owned by TabsHost (single seam, handles
  // programmatic focus too). TabLink only needs to set activeId via
  // openTab(); the URL follows automatically. openTab resolves the title
  // and surfaces the cap toast.
  const activate = useCallback(
    (forceNew) => openTab(to, { forceNew }),
    [openTab, to],
  );

  const handleClick = useCallback(
    (e) => {
      // Allow consumers to run their own click side-effects (e.g. closing
      // a sidebar drawer on mobile) before we navigate. They can call
      // e.preventDefault() to suppress our handling entirely.
      if (onClickProp) onClickProp(e);
      if (e.defaultPrevented) return;
      // Only handle primary-button. Other buttons / modifier combos for
      // "open in new browser tab" should fall through to the browser.
      if (e.button !== 0) return;
      e.preventDefault();
      const forceNew = e.ctrlKey || e.metaKey || e.shiftKey;
      activate(forceNew);
    },
    [onClickProp, activate],
  );

  const handleAuxClick = useCallback(
    (e) => {
      if (e.button === 1) {
        e.preventDefault();
        activate(true);
      }
    },
    [activate],
  );

  return (
    <a
      ref={ref}
      href={to}
      className={className}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      {...rest}
    >
      {children}
    </a>
  );
});

export default TabLink;
